import {
  DeleteOutlineRounded,
  DescriptionOutlined,
  DriveFileRenameOutlineRounded,
  EditRounded,
} from '@mui/icons-material'
import { Box, Button, IconButton, Modal, TextField, Tooltip, Typography, useMediaQuery } from '@mui/material'
import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { fullName } from '../../../domain/Account/AccountDisplay'
import { getReadableCustomerTemplateType } from '../../../domain/Customer/CustomerDisplay'
import { backendClient } from '../../../interactors/clients/client'
import { CustomerPdfTemplateDto } from '../../../interactors/gen/backendClient'
import { useAccountsStore } from '../../../store/AccountsStore'
import { useGlobalSnackbarStore } from '../../../store/GlobalSnackBarStore'
import { DisplayChipLikeTextField } from '../../common/DisplayChipLikeTextField'
import { blueSky, theme } from '../../theme'

interface TemplateItemProps {
  template: CustomerPdfTemplateDto
  setTemplatesList: React.Dispatch<React.SetStateAction<CustomerPdfTemplateDto[]>>
}

export const PdfTemplateItem: React.FC<TemplateItemProps> = ({ template, setTemplatesList }) => {
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [renameModalOpen, setRenameModalOpen] = useState(false)
  const [newName, setNewName] = useState(template.name)

  const globalSnackbar = useGlobalSnackbarStore()
  const navigate = useNavigate()
  const accountsStore = useAccountsStore()
  const members = accountsStore.members
  const member = members.find((m) => m.id === template.uploadedBy)
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const showAuthorInline = useMediaQuery('(min-width:1100px)')
  const creationDate = new Date(template.uploadedAt).toLocaleDateString()
  const author = member ? fullName(member) : '-'

  const handleDelete = async () => {
    await backendClient.delete(`/customers/pdf-templates/${template.id}`)
    setTemplatesList((prev) => prev.filter((t) => t.id !== template.id))
    setDeleteModalOpen(false)
    globalSnackbar.triggerSuccessMessage('Modèle PDF supprimé avec succès')
  }

  const handleRename = async () => {
    // Implement rename logic here, e.g., updating the template's name in the backend.
    const updatedTemplate = { ...template, name: newName }
    setTemplatesList((prev) => prev.map((t) => (t.id === template.id ? updatedTemplate : t)))
    await backendClient.put(`customers/pdf-templates/${template.id}`, updatedTemplate)
    setRenameModalOpen(false)
    globalSnackbar.triggerSuccessMessage('Modèle PDF renommé avec succès')
  }

  return (
    <>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '36px minmax(0, 1fr) auto',
          columnGap: { xs: 1, sm: 1.5 },
          alignItems: 'center',
          py: { xs: 1.25, sm: 1.5 },
          borderBottom: '1px solid rgba(122, 129, 151, 0.16)',
          cursor: 'pointer',
          '&:hover': { backgroundColor: 'rgba(105, 141, 172, 0.06)' },
          borderRadius: 2.5,
          px: { xs: 1, sm: 1.25 },
        }}
        onClick={() =>
          navigate('/association/zone', {
            state: {
              template: {
                ...template,
                zones: JSON.stringify(template.zones),
                variables: JSON.stringify(template.variables),
              },
            },
          })
        }
      >
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          sx={{
            width: { xs: 32, sm: 36 },
            height: { xs: 32, sm: 36 },
            borderRadius: 2.5,
            backgroundColor: 'rgba(105, 141, 172, 0.10)',
            border: '1px solid rgba(105, 141, 172, 0.16)',
          }}
        >
          <DescriptionOutlined sx={{ width: { xs: 18, sm: 20 }, height: { xs: 18, sm: 20 }, color: blueSky }} />
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: isMobile
              ? 'minmax(0, 1fr)'
              : showAuthorInline
              ? 'minmax(210px, 0.95fr) minmax(190px, 0.86fr) minmax(125px, 0.58fr) minmax(145px, 0.7fr)'
              : 'minmax(210px, 1fr) minmax(190px, 0.86fr) minmax(125px, 0.58fr)',
            columnGap: { sm: 1.5, md: 2.25 },
            alignItems: 'center',
            minWidth: 0,
          }}
        >
          <Box sx={{ minWidth: 0, display: 'flex', alignItems: 'center', minHeight: 38 }}>
            <Typography
              sx={{
                color: '#203864',
                fontSize: { xs: 13.25, sm: 13.5, md: 14 },
                fontWeight: 700,
                lineHeight: 1.25,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
              noWrap
              title={template.name}
            >
              {template.name}
            </Typography>
          </Box>

          {!isMobile && (
            <Box sx={{ minWidth: 0, display: 'flex', alignItems: 'center', minHeight: 38 }}>
              <DisplayChipLikeTextField
                label=""
                value={template.type ? getReadableCustomerTemplateType(template.type) : ''}
                fullWidth
                documentPage
                color={blueSky}
                compact
                borderless
                emphasis
                maxChipWidth={250}
              />
            </Box>
          )}

          {!isMobile && (
            <Box sx={{ minHeight: 38, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <Typography
                variant="caption"
                sx={{ color: '#7A8197', fontSize: { xs: 10.5, sm: 11 }, fontWeight: 500, lineHeight: 1.2, mb: 0.25 }}
              >
                Date de création
              </Typography>
              <Typography
                sx={{ color: '#203864', fontSize: { xs: 12.25, sm: 12.75, md: 13 }, fontWeight: 400, lineHeight: 1.25 }}
              >
                {creationDate}
              </Typography>
            </Box>
          )}

          {!isMobile && showAuthorInline && (
            <Box sx={{ minHeight: 38, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <Typography
                variant="caption"
                sx={{ color: '#7A8197', fontSize: { xs: 10.5, sm: 11 }, fontWeight: 500, lineHeight: 1.2, mb: 0.25 }}
              >
                Auteur
              </Typography>
              <Typography
                sx={{
                  color: '#203864',
                  fontSize: { xs: 12.25, sm: 12.75, md: 13 },
                  fontWeight: 400,
                  lineHeight: 1.25,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
                title={author}
              >
                {author}
              </Typography>
            </Box>
          )}
        </Box>

        <Box
          sx={{ display: 'flex', alignItems: 'center', justifyContent: { xs: 'flex-start', md: 'flex-end' }, gap: 0.5 }}
          onClick={(e) => e.stopPropagation()}
        >
          <Tooltip title="Modifier" arrow>
            <IconButton
              size="small"
              onClick={() =>
                navigate('/association/zone', {
                  state: {
                    template: {
                      ...template,
                      zones: JSON.stringify(template.zones),
                      variables: JSON.stringify(template.variables),
                    },
                  },
                })
              }
              sx={{
                width: { xs: 28, sm: 32 },
                height: { xs: 28, sm: 32 },
                borderRadius: 2,
                color: '#203864',
                backgroundColor: 'rgba(32, 56, 100, 0.08)',
                '&:hover': { backgroundColor: 'rgba(32, 56, 100, 0.14)' },
              }}
            >
              <EditRounded fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Renommer" arrow>
            <IconButton
              size="small"
              onClick={() => setRenameModalOpen(true)}
              sx={{
                width: { xs: 28, sm: 32 },
                height: { xs: 28, sm: 32 },
                borderRadius: 2,
                color: '#203864',
                backgroundColor: 'rgba(32, 56, 100, 0.08)',
                '&:hover': { backgroundColor: 'rgba(32, 56, 100, 0.14)' },
              }}
            >
              <DriveFileRenameOutlineRounded fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Supprimer" arrow>
            <IconButton
              size="small"
              onClick={() => setDeleteModalOpen(true)}
              sx={{
                width: { xs: 28, sm: 32 },
                height: { xs: 28, sm: 32 },
                borderRadius: 2,
                color: 'error.main',
                backgroundColor: 'rgba(220, 53, 69, 0.08)',
                '&:hover': { backgroundColor: 'rgba(220, 53, 69, 0.14)' },
              }}
            >
              <DeleteOutlineRounded fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Rename Modal */}
      <Modal
        open={renameModalOpen}
        onClose={() => setRenameModalOpen(false)}
        aria-labelledby="rename-modal-title"
        aria-describedby="rename-modal-description"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            p: 4,
          }}
        >
          <Typography id="rename-modal-title" variant="h6" component="h2">
            Renommer le modèle
          </Typography>
          <TextField
            fullWidth
            margin="normal"
            label="Nouveau nom"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
          />
          <Box mt={4} display="flex" justifyContent="space-between">
            <Button onClick={() => setRenameModalOpen(false)} variant="outlined" color="primary">
              Annuler
            </Button>
            <Button onClick={handleRename} variant="contained" color="primary">
              Renommer
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* Delete Modal */}
      <Modal
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        aria-labelledby="delete-modal-title"
        aria-describedby="delete-modal-description"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            p: 4,
          }}
        >
          <Typography id="delete-modal-title" variant="h6" component="h2">
            Supprimer le modèle
          </Typography>
          <Typography id="delete-modal-description" sx={{ mt: 2 }}>
            Êtes-vous sûr de vouloir supprimer ce modèle ?
          </Typography>
          <Box mt={4} display="flex" justifyContent="space-between">
            <Button onClick={() => setDeleteModalOpen(false)} variant="outlined" color="primary">
              Annuler
            </Button>
            <Button onClick={handleDelete} variant="contained" color="error">
              Supprimer
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  )
}
